import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "@heroui/react";
import React from "react";

import FirebaseAdapter from "@wc/adapters/firebase";
import { toggleReaction } from "@wc/domain/teams/core/reactions-service";
import { useEventReactions } from "@wc/domain/teams/hooks/use-event-reactions";

/**
 * Event reactions component showing emoji reactions and picker
 * @param {object} props Component props
 * @param {string} props.teamId The team ID
 * @param {string} props.eventId The event ID
 * @param {Array<{userId: string, displayName: string}>} props.teamMembers Team members for name lookup
 * @returns {React.ReactElement} Reactions UI
 */
export default function EventReactions({ teamId, eventId, teamMembers = [] }) {
  const { groupedReactions, loading } = useEventReactions(teamId, eventId);
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  const [isToggling, setIsToggling] = React.useState(false);

  const currentUserId = FirebaseAdapter().authn.getCurrentUser()?.uid;

  const memberMap = React.useMemo(() => {
    const map = {};
    for (const member of teamMembers) {
      map[member.userId] = member.displayName;
    }
    return map;
  }, [teamMembers]);

  const handleToggleReaction = async (emoji) => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      await toggleReaction(teamId, eventId, emoji);
    } catch (error) {
      console.error("Failed to toggle reaction:", error);
    } finally {
      setIsToggling(false);
    }
  };

  const handleEmojiSelect = async (emojiData) => {
    setIsPickerOpen(false);
    await handleToggleReaction(emojiData.native);
  };

  if (loading) {
    return null;
  }

  const reactionEmojis = Object.keys(groupedReactions);

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {reactionEmojis.map((emoji) => {
        const reactions = groupedReactions[emoji];
        const userNames = reactions
          .map((r) => memberMap[r.userId] || "Unknown")
          .join(", ");
        const hasCurrentUserReacted = reactions.some(
          (r) => r.userId === currentUserId,
        );

        return (
          <Tooltip key={emoji} content={userNames} delay={500}>
            <Button
              size="sm"
              variant={hasCurrentUserReacted ? "flat" : "light"}
              className={`h-6 min-w-0 gap-1 px-1.5 ${hasCurrentUserReacted ? "bg-primary-100" : ""}`}
              onPress={() => handleToggleReaction(emoji)}
              isDisabled={isToggling}
            >
              <span>{emoji}</span>
              {reactions.length > 1 && (
                <span className="text-xs">{reactions.length}</span>
              )}
            </Button>
          </Tooltip>
        );
      })}

      <Popover
        isOpen={isPickerOpen}
        onOpenChange={setIsPickerOpen}
        placement="bottom-start"
      >
        <PopoverTrigger>
          <Button
            size="sm"
            variant="light"
            className="text-default-400 h-6 min-w-0 px-1.5"
            isDisabled={isToggling}
          >
            +
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Picker
            data={data}
            onEmojiSelect={handleEmojiSelect}
            theme="auto"
            previewPosition="none"
            skinTonePosition="none"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
